import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { useParams, useNavigate, Link } from "react-router-dom";
import ImageCarousel from "../components/ImageCarousel";
import Rating from "../components/Rating";
import {
  useGetProductDetailsQuery,
  useCreateReviewMutation,
  useAddInquiryMutation,
  useAnswerInquiryMutation,
} from "../slices/productsApiSlice.js";
import {
  useAddProductToWishlistMutation,
  useFollowCreatorMutation,
  useGetFollowingCreatorsQuery,
  useGetUserCreatorByTagQuery,
  useGetUserWishlistQuery,
  useRemoveProductFromWishlistMutation,
  useUnfollowCreatorMutation,
} from "../slices/usersApiSlice.js";
import { addToCart } from "../slices/cartSlice.js";
import Loader from "../components/Loader";
import Message from "../components/Message";
import Meta from "../components/Meta";
import { useGetExchangeRatesQuery } from "../slices/exchangeSlice.js";
import { formatPrice } from "../utils/formatPrice.js";
import { languageFlags } from "../constants.js";
import { FaStar } from "react-icons/fa";
import UserAvatar from "../components/UserAvatar.jsx";
import { addRecentlySeenProduct } from "../utils/recentlySeen.js";
import { trackEvent } from "../lib/analytics";

const formatInquiryDisplayName = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";
  const first = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || "";
  return `${first} ${lastInitial}.`;
};

const formatReviewDisplayName = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || "";
  const first = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || "";
  return `${first} ${lastInitial}.`;
};

function ProductScreen() {
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [question, setQuestion] = useState("");
  const [showCartModal, setShowCartModal] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [answerText, setAnswerText] = useState("");
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);

  const { data: product, isLoading, error, refetch } = useGetProductDetailsQuery(productId);
  const [answerInquiry, { isLoading: isAnswering }] = useAnswerInquiryMutation();
  const [addInquiry, { isLoading: addingInquiry }] = useAddInquiryMutation();
  const [createReview, { isLoading: loadingProductReview }] = useCreateReviewMutation();

  const { userInfo } = useSelector((state) => state.auth);
  const creatorTagFromOwnerUrl = String(product?.ownerUrl || "")
    .replace(/^\/+/, "")
    .split("/")
    .pop();
  const { data: creatorData, refetch: refetchCreatorData } = useGetUserCreatorByTagQuery(
    creatorTagFromOwnerUrl,
    { skip: !creatorTagFromOwnerUrl }
  );
  const { data: followingCreators, refetch: refetchFollowing } = useGetFollowingCreatorsQuery(undefined, {
    skip: !userInfo,
  });
  const { data: wishlistData, refetch: refetchWishlist } = useGetUserWishlistQuery(userInfo?._id, {
    skip: !userInfo?._id,
  });
  const [addProductToWishlist, { isLoading: addingToWishlist }] =
    useAddProductToWishlistMutation();
  const [removeProductFromWishlist, { isLoading: removingFromWishlist }] =
    useRemoveProductFromWishlistMutation();
  const [followCreator, { isLoading: followingBusy }] = useFollowCreatorMutation();
  const [unfollowCreator, { isLoading: unfollowingBusy }] = useUnfollowCreatorMutation();

  useEffect(() => {
    if (product?.inquiries) {
      setInquiries(product.inquiries);
    }
  }, [product]);

  useEffect(() => {
    if (!product?._id) return;
    trackEvent("product_viewed", {
      product_id: product._id,
      creator_id: product.user || "",
      is_free: Boolean(product.isFree) || Number(product.price) <= 0,
    });
    addRecentlySeenProduct(userInfo?._id, {
      _id: product._id,
      name: product.name,
      images: product.images || [],
      ownerName: product.ownerName || "",
      ownerUrl: product.ownerUrl || "",
      ownerImage: product.ownerImage || "",
      userColor: product.userColor || "",
      languages: product.languages || [],
      category: product.category || [],
      rating: Number(product.rating || 0),
      numReviews: Number(product.numReviews || 0),
      price: Number(product.price || 0),
      isFree: Boolean(product.isFree) || Number(product.price) <= 0,
      user: product.user || "",
    });
  }, [product, userInfo?._id]);

  const addToCartHandler = () => {
    trackEvent("add_to_cart", {
      product_id: product._id,
      quantity: qty,
      is_free: Boolean(product.isFree) || Number(product.price) <= 0,
    });
    dispatch(addToCart({ ...product, qty }));
    setShowCartModal(true);
  };

  const handleAnswerSubmit = async (inquiryId) => {
    if (!answerText.trim()) {
      toast.error("Inquiry cannot be empty.");
      return;
    }
    try {
      await answerInquiry({
        productId: product._id,
        inquiryId,
        comment: answerText,
      }).unwrap();
      refetch();
      toast.success("Answer submitted successfully!");
      setAnswerText("");
      setSelectedInquiryId(null);
    } catch (submitErr) {
      toast.error(submitErr?.data?.message || "Failed to submit answer");
    }
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      toast.error("Inquiry cannot be empty.");
      return;
    }
    try {
      await addInquiry({ productId: product._id, comment: question }).unwrap();
      toast.success("Inquiry submitted successfully!");
      setQuestion("");
      refetch();
    } catch (submitErr) {
      toast.error(submitErr?.data?.message || "Error submitting inquiry");
    }
  };

  const submitReviewHandler = async (e) => {
    e.preventDefault();
    try {
      await createReview({
        productId,
        rating,
        comment,
      }).unwrap();
      refetch();
      toast.success("Review Submitted");
      setRating(0);
      setComment("");
    } catch (submitErr) {
      toast.error(submitErr?.data?.message || submitErr.error);
    }
  };

  const { data: exchangeData } = useGetExchangeRatesQuery();
  const preferredCurrency = userInfo?.preferredCurrency || "USD";

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;
  if (!product) return null;

  const displayPrice = formatPrice(product.price, preferredCurrency, exchangeData);
  const isFreeProduct = Boolean(product?.isFree) || Number(product?.price) <= 0;
  const isOwnProduct = Boolean(userInfo && userInfo._id === product.user);
  const ownerUrl = String(product?.ownerUrl || "").startsWith("/")
    ? product.ownerUrl
    : `/${String(product?.ownerUrl || "")}`;
  const isWishlisted = Boolean(
    userInfo &&
      Array.isArray(wishlistData) &&
      wishlistData.some((entry) => String(entry.productId) === String(productId))
  );
  const wishlistBusy = addingToWishlist || removingFromWishlist;
  const creatorFollowerCount = Number(creatorData?.followerCount || 0);
  const creatorUserId = creatorData?.userId;
  const isOwnCreatorPage = Boolean(userInfo && creatorUserId && String(creatorUserId) === String(userInfo._id));
  const isFollowingCreator = Boolean(
    userInfo &&
      creatorUserId &&
      Array.isArray(followingCreators) &&
      followingCreators.some((entry) => String(entry._id) === String(creatorUserId))
  );

  const wishlistToggleHandler = async () => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    try {
      if (isWishlisted) {
        await removeProductFromWishlist(productId).unwrap();
        toast.success("Removed from wishlist");
      } else {
        await addProductToWishlist(productId).unwrap();
        trackEvent("wishlist_added", { product_id: productId });
        toast.success("Added to wishlist");
      }
      refetchWishlist();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update wishlist");
    }
  };

  const followToggleHandler = async () => {
    if (!userInfo) {
      navigate("/login");
      return;
    }
    if (!creatorUserId || isOwnCreatorPage) return;
    try {
      if (isFollowingCreator) {
        await unfollowCreator(creatorUserId).unwrap();
        toast.success("Unfollowed creator");
      } else {
        await followCreator(creatorUserId).unwrap();
        trackEvent("creator_followed", { creator_id: creatorUserId });
        toast.success("Creator followed");
      }
      refetchFollowing();
      refetchCreatorData();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update follow status");
    }
  };

  return (
    <>
      {showCartModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-2">
              <h3 className="m-0 text-lg font-semibold text-slate-900">Product Added to Cart</h3>
              <button
                type="button"
                onClick={() => setShowCartModal(false)}
                className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                x
              </button>
            </div>
            <p className="mb-1 text-sm text-slate-700">
              <strong>{product.name}</strong> has been added to your cart.
            </p>
            <p className="mb-4 text-sm text-slate-600">What would you like to do next?</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCartModal(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Continue Shopping
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCartModal(false);
                  navigate("/cart");
                }}
                className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                Go to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      <Meta title={product.name} />

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-6">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <ImageCarousel images={product.images} />
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mx-auto max-w-md text-center">
              <h1 className="mb-2 text-2xl font-semibold text-slate-900">{product.name}</h1>

              <div className="mb-2 flex flex-wrap items-center justify-center gap-3">
                <Link to={ownerUrl} className="flex items-center gap-2 no-underline">
                  <UserAvatar
                    user={{
                      name: product.ownerName,
                      profileImage: product.ownerImage,
                      userColor: product.userColor,
                    }}
                    size={32}
                  />
                  <span className="text-sm font-medium text-slate-700 hover:text-sky-700">
                    {product.ownerName}
                  </span>
                </Link>
                <div className="flex items-center gap-1">
                  {product.languages?.map((lang, index) =>
                    languageFlags[lang] ? (
                      <img
                        key={index}
                        src={languageFlags[lang]}
                        alt={lang}
                        title={lang}
                        className="h-4 w-6 rounded-sm border border-slate-200 object-cover"
                      />
                  ) : null
                  )}
                </div>
              </div>

              <div className="mb-2 flex flex-col items-center gap-2 text-sm text-slate-600">
                <span>
                  {creatorFollowerCount} follower{creatorFollowerCount === 1 ? "" : "s"}
                </span>
                {!isOwnCreatorPage && (
                  <div className="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      onClick={followToggleHandler}
                      disabled={followingBusy || unfollowingBusy}
                      className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                        isFollowingCreator
                          ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          : "bg-sky-600 text-white hover:bg-sky-700"
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      {isFollowingCreator ? "Unfollow" : "Follow"}
                    </button>
                    <p className="max-w-[250px] text-center text-[11px] leading-4 text-slate-500">
                      Follow this creator to keep track of their catalog updates more easily.
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-2 flex flex-wrap gap-1.5">
                {product.category && product.category.length > 0 ? (
                  product.category.map((cat, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
                    >
                      {cat}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-500">No keywords</span>
                )}
              </div>

              <div className="mb-2">
                <Rating value={product.rating} text={`${product.numReviews} reviews`} />
              </div>

              <p className="m-0 text-sm leading-6 text-slate-600">{product.description}</p>
            </div>

            <div className="mx-auto mt-3 max-w-md border-t border-slate-100 pt-3 text-center">
              <div className="flex flex-col items-center justify-center gap-1 pb-2">
                <span className="text-sm font-medium text-slate-500">Price</span>
                <strong className="text-lg text-slate-900">{isFreeProduct ? "Free" : displayPrice}</strong>
              </div>

              {product.countInStock > 0 && (
                <div className="mt-2">
                  <label className="mb-1.5 block text-sm font-medium text-slate-600">Licenses</label>
                  <select
                    value={qty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    className="mx-auto block w-[70%] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  >
                    {[...Array(10).keys()].map((ele) => (
                      <option key={ele + 1} value={ele + 1}>
                        {ele + 1}
                      </option>
                    ))}
                  </select>
                  <p className="mx-auto mt-2 w-[70%] text-xs leading-5 text-slate-500">
                    Choose how many download licenses you want to purchase for this resource.
                  </p>
                </div>
              )}

              <div className="mt-3 space-y-2.5">
                <button
                  type="button"
                  className="mx-auto inline-flex w-[70%] items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={product.countInStock === 0 || isOwnProduct}
                  onClick={addToCartHandler}
                >
                  Add To Cart
                </button>
                {isOwnProduct && (
                  <div className="text-xs text-slate-500">You cannot purchase your own product.</div>
                )}

                <button
                  type="button"
                  className="mx-auto inline-flex w-[70%] items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                  disabled={isOwnProduct || wishlistBusy}
                  onClick={wishlistToggleHandler}
                >
                  <FaStar className="mr-2" />
                  {isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-2 text-xl font-semibold text-slate-900">Product Details</h2>
        <p className="m-0 whitespace-pre-line text-sm leading-6 text-slate-600">{product.longDescription}</p>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-xl font-semibold text-slate-900">Inquiries</h2>
          {inquiries.length === 0 && (
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              No inquiries have been made yet. Be the first to ask!
            </p>
          )}

          <div className="space-y-3">
            {inquiries.map((inquiry) => (
              <article key={inquiry._id} className="space-y-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <UserAvatar user={inquiry.question} />
                    <strong className="text-sm text-slate-800">
                      {formatInquiryDisplayName(inquiry.question.name)}
                    </strong>
                  </div>
                  <p className="m-0 text-sm text-slate-700">{inquiry.question.comment}</p>
                </div>

                {inquiry.answer && inquiry.answer.comment ? (
                  <div className="ml-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <UserAvatar user={inquiry.answer} />
                      <strong className="text-sm text-slate-800">
                        {formatInquiryDisplayName(inquiry.answer.name)}
                      </strong>
                    </div>
                    <p className="m-0 text-sm text-slate-700">{inquiry.answer.comment}</p>
                  </div>
                ) : (
                  userInfo &&
                  userInfo._id === product.user && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAnswerSubmit(inquiry._id);
                      }}
                      className="ml-4 rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Answer this inquiry
                      </label>
                      <textarea
                        rows={2}
                        value={selectedInquiryId === inquiry._id ? answerText : ""}
                        onChange={(e) => {
                          setSelectedInquiryId(inquiry._id);
                          setAnswerText(e.target.value);
                        }}
                        placeholder="Type your answer..."
                        required
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                      />
                      <button
                        type="submit"
                        disabled={isAnswering}
                        className="mt-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        {isAnswering ? "Submitting..." : "Submit Answer"}
                      </button>
                    </form>
                  )
                )}
              </article>
            ))}
          </div>

          <div className="mt-4">
            {userInfo ? (
              userInfo._id !== product.user ? (
                <form onSubmit={handleQuestionSubmit} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Ask a Question</label>
                  <textarea
                    rows={3}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                  <button
                    type="submit"
                    disabled={addingInquiry}
                    className="mt-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Submit
                  </button>
                </form>
              ) : (
                <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  As the activity owner you cannot post questions about it.
                </p>
              )
            ) : (
              <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                You need to be logged in to post questions. <Link to="/signup">Sign up here</Link>.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-xl font-semibold text-slate-900">Reviews</h2>
          {product.reviews.length === 0 && (
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Be the first to write a Review!
            </p>
          )}

          <div className="space-y-3">
            {product.reviews.map((review) => (
              <article key={review._id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <strong className="text-sm text-slate-800">{formatReviewDisplayName(review.name)}</strong>
                <div className="mt-1">
                  <Rating value={review.rating} />
                </div>
                <p className="mt-1 text-xs text-slate-500">{review.createdAt.substring(0, 10)}</p>
                <p className="m-0 text-sm text-slate-700">{review.comment}</p>
              </article>
            ))}
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="mb-3 text-lg font-semibold text-slate-900">Write a Customer Review</h3>
            {loadingProductReview && <Loader />}

            {!userInfo && (
              <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Please <Link to="/login">sign in</Link> to write a review.
              </p>
            )}

            {userInfo && userInfo._id === product.user && (
              <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                As the product creator, you cannot review your own product.
              </p>
            )}

            {userInfo && userInfo._id !== product.user && (
              <form onSubmit={submitReviewHandler} className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Rating</label>
                  <select
                    value={rating}
                    onChange={(e) => setRating(Number(e.target.value))}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="">Select...</option>
                    <option value="1">1 - Poor</option>
                    <option value="2">2 - Fair</option>
                    <option value="3">3 - Good</option>
                    <option value="4">4 - Very Good</option>
                    <option value="5">5 - Excellent</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Comment</label>
                  <textarea
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                </div>

                <button
                  disabled={loadingProductReview}
                  type="submit"
                  className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Submit
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default ProductScreen;

